import { getTransactionsAction } from "./transactionsReducer";
import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getTransactions } from "./transactionsSelectors";
import { IPosition, POSITION_STATUS } from "./transactions.t";
import { Badge, Button, Col, FormGroup, FormSelect, Row, Table } from "react-bootstrap";
import amountFormat from "../../utils/amountFormat";
import DatePicker from "../../components/DatePicker/DatePicker";
import moment from "moment-timezone";
import { TransactionsFilterPresets, TransactionsStyled } from "./styled/TransactionsStyled";
import { useLocation } from "react-router";
import appHistory from "../../app/appHistory";
import _ from "lodash";
import { BinIcon } from "../../components/Icon/Icon";
import { Link } from "react-router-dom";

const Transactions = () => {
    const dispatch = useDispatch();
    const transactions: IPosition[] = useSelector(getTransactions);
    const { search } = useLocation();

    const [from, setFrom] = useState(moment().utc().startOf('day').toDate());
    const [to, setTo] = useState(moment().utc().endOf('day').toDate());

    const searchParams = useMemo(() => {
        const urlParams = new URLSearchParams(search);
        return {
            from: urlParams.get('from'),
            to: urlParams.get('to'),
            limit: +(urlParams.get('limit') || 50),
            offset: +(urlParams.get('offset') || 0),
            search: urlParams.get('search'),
            status: urlParams.get('status'),
        };
    }, [search]);

    const onChangeFromHandler = useCallback((date) => {
        const urlParams = new URLSearchParams(search);
        urlParams.set('from', moment(date).utc().startOf('day').toISOString());
        appHistory.push({
            search: urlParams.toString(),
        });
    }, [search]);

    const onChangeToHandler = useCallback((date) => {
        const urlParams = new URLSearchParams(search);
        urlParams.set('to', moment(date).utc().endOf('day').toISOString());
        appHistory.push({
            search: urlParams.toString(),
        });
    }, [search]);

    const changeParam = useCallback((name: string, value: number) => {
        const urlParams = new URLSearchParams(search);
        urlParams.set(name, _.max([value, 0]).toString());
        appHistory.push({
            search: urlParams.toString(),
        });
    }, [search]);

    const presetClickHandler = useCallback((e) => {
        e.stopPropagation();
        const name = e.target.dataset.name;
        const urlParams = new URLSearchParams(search);
        switch (name) {
            case 'today':
                urlParams.set('from', moment().utc().startOf('day').toISOString());
                urlParams.set('to', moment().utc().endOf('day').toISOString());
                appHistory.push({
                    search: urlParams.toString(),
                });
                break;
            case 'this-week':
                urlParams.set('from', moment().utc().startOf('week').toISOString());
                urlParams.set('to', moment().utc().endOf('week').toISOString());
                appHistory.push({
                    search: urlParams.toString(),
                });
                break;
            case 'this-month':
                urlParams.set('from', moment().utc().startOf('month').toISOString());
                urlParams.set('to', moment().utc().endOf('month').toISOString());
                appHistory.push({
                    search: urlParams.toString(),
                });
                break;
        }
    }, [search]);

    const filterChangeHandler = useCallback((e) => {
        const field = e.target.name;
        const value = e.target.value;
        changeParam(field, value);
    }, [changeParam]);

    const clearFilterHandler = useCallback((e) => {
        e.preventDefault();
        const name = e.currentTarget.dataset.filter;
        const urlParams = new URLSearchParams(search);
        urlParams.delete(name);
        appHistory.push({
            search: urlParams.toString(),
        });
    }, [search]);

    useEffect(() => {
        dispatch(getTransactionsAction(searchParams));
    }, [dispatch, searchParams]);

    useEffect(() => {
        const urlParams = new URLSearchParams(search);
        const from = urlParams.get('from');
        const to = urlParams.get('to');
        if (from) setFrom(moment(from).toDate());
        if (to) setTo(moment(to).toDate());
    }, [search]);

    return (
        <TransactionsStyled>
            <h3>Transactions <Badge bg={'primary'}>{transactions.length}</Badge></h3>

            <FormGroup className={'p-3'} style={{ background: 'var(--color-bg-modal)' }}>
                <Row>
                    <Col>From</Col>
                    <Col>To</Col>
                    <Col>Status</Col>
                    <Col>
                        {
                            searchParams.limit > 0 && <>Records {searchParams.limit} from {searchParams.offset}</>
                        }
                    </Col>
                </Row>
                <Row>
                    <Col><DatePicker selected={from} onChange={onChangeFromHandler}/></Col>
                    <Col><DatePicker selected={to} onChange={onChangeToHandler}/></Col>
                    <Col>
                        <FormSelect name={'status'} onChange={filterChangeHandler}>
                            <option value={''}>-</option>
                            {
                                [POSITION_STATUS.OPEN, POSITION_STATUS.CLOSE].map(status => <Fragment key={status}>
                                    <option value={status} selected={searchParams.status === status}>{status}</option>
                                </Fragment>)
                            }
                        </FormSelect>
                    </Col>
                    <Col>
                        {
                            searchParams.limit > 0 && <>
                                <Button variant={'secondary'} name={'offset'}
                                        disabled={transactions.length < searchParams.limit}
                                        onClick={() => changeParam('offset', searchParams.offset + searchParams.limit)}>«</Button>
                                <Button variant={'secondary'} name={'offset'}
                                        disabled={searchParams.offset <= 0}
                                        onClick={() => changeParam('offset', searchParams.offset - searchParams.limit)}>»</Button>
                            </>
                        }
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <TransactionsFilterPresets>
                            <div data-name={'today'} onClick={presetClickHandler}>Today</div>
                            <div data-name={'this-week'} onClick={presetClickHandler}>This week</div>
                            <div data-name={'this-month'} onClick={presetClickHandler}>This month</div>
                        </TransactionsFilterPresets>
                    </Col>
                    <Col>
                    </Col>
                    <Col>
                    </Col>
                </Row>
            </FormGroup>

            <Table striped bordered hover variant="dark">
                <thead>
                <tr>
                    <th>Time</th>
                    <th>Position ID</th>
                    <th>Side</th>
                    <th>Symbol</th>
                    <th>Quantity</th>
                    <th>Open Time<br/>Close Time</th>
                    <th>Open Price<br/>Close Price</th>
                    <th>Status {
                        searchParams.status && <Link to={'#'} data-filter={'status'}
                                                     onClick={clearFilterHandler}><BinIcon
                        style={{color: 'var(--color-negative)'}}
                            name={'IcCloseWhite'}/></Link>
                    }</th>
                    <th>PL</th>
                </tr>
                </thead>
                <tbody>
                {
                    transactions.map(item => <Fragment key={item.id}>
                        <tr>
                            <td>{item.created_at}</td>
                            <td>{item.position_id}</td>
                            <td>{item.side}</td>
                            <td>{item.symbol}</td>
                            <td>{amountFormat(item.quantity, 2)}</td>
                            <td>{item.open_time}<br/>{item.close_time}</td>
                            <td>{amountFormat(item.open_price, 6)}<br/>{item.close_price ? amountFormat(item.close_price, 6) : '-'}
                            </td>
                            <td>{item.status}</td>
                            <td style={{
                                color: item.pl > 0 ? 'var(--color-positive)' : item.pl < 0 ? 'var(--color-negative)' : '',
                            }}>{amountFormat(item.pl, 6, '$')}</td>
                        </tr>
                    </Fragment>)
                }
                </tbody>
            </Table>
        </TransactionsStyled>
    );
}

export default Transactions;
